import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, type Location as RouterLocation } from 'react-router-dom'
import { AuthErrorScreen, AuthLoadingScreen } from '@/auth/AuthErrorScreen'
import { useAuth } from '@/auth/AuthContext'
import { mapFirebaseAuthError } from '@/auth/errors'
import {
  signInWithEmail,
  signInWithGoogle,
} from '@/auth/firebase'
import { consumeAuthRedirectPath, persistAuthRedirectPath } from '@/auth/storage'
import { isRegistrationEnabled } from '@/config/features'
import { useTranslation } from '@/i18n'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { authState, loading } = useAuth()
  const { t } = useTranslation()
  const from = (location.state as { from?: RouterLocation } | null)?.from
  const [showEmailForm, setShowEmailForm] = useState(!isRegistrationEnabled)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loadingEmail, setLoadingEmail] = useState(false)
  const [loadingGoogle, setLoadingGoogle] = useState(false)

  useEffect(() => {
    if (!from?.pathname) return
    const target = `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`
    persistAuthRedirectPath(target)
  }, [from])

  useEffect(() => {
    if (loading) return

    if (authState === 'ready') {
      navigate(consumeAuthRedirectPath('/dashboard'), { replace: true })
      return
    }

    if (authState === 'verifyEmail') {
      navigate('/verifieer-email', { replace: true })
      return
    }

    if (authState === 'needsProfile') {
      navigate('/account-aanmaken', { replace: true })
    }
  }, [authState, loading, navigate])

  const submitting = loadingEmail || loadingGoogle

  async function handleEmailSubmit(event: React.FormEvent) {
    event.preventDefault()
    setError('')
    setLoadingEmail(true)

    try {
      await signInWithEmail(email, password)
    } catch (submitError) {
      setError(t(mapFirebaseAuthError(submitError, 'login')))
    } finally {
      setLoadingEmail(false)
    }
  }

  async function handleGoogleSignIn() {
    setError('')
    setLoadingGoogle(true)

    try {
      await signInWithGoogle()
    } catch (submitError) {
      const message = t(mapFirebaseAuthError(submitError, 'social'))
      if (message) setError(message)
    } finally {
      setLoadingGoogle(false)
    }
  }

  if (loading) return <AuthLoadingScreen fullscreen={false} />
  if (authState === 'error') return <AuthErrorScreen fullscreen={false} />
  if (authState !== 'notLoggedIn') return <AuthLoadingScreen fullscreen={false} />

  if (!showEmailForm) {
    return (
      <div className="mx-auto w-full max-w-md">
        <h2 className="mb-2 text-xl font-semibold text-surface-100">{t('Inloggen')}</h2>
        <p className="mb-6 text-sm text-surface-400">{t('Kies hoe je verder wilt gaan.')}</p>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={submitting}
          className="mb-4 flex w-full items-center justify-center gap-3 rounded-xl border border-surface-700 bg-surface-800 px-4 py-3 text-sm font-medium text-surface-100 transition hover:border-surface-500 hover:bg-surface-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loadingGoogle ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-surface-500 border-t-surface-100" />
          ) : (
            <GoogleIcon />
          )}
          {t('Doorgaan met Google')}
        </button>

        <div className="mb-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-surface-800" />
          <span className="text-xs text-surface-500">{t('of')}</span>
          <div className="h-px flex-1 bg-surface-800" />
        </div>

        {error ? (
          <p className="mb-4 rounded-lg bg-red-950/60 px-4 py-2.5 text-sm text-red-400">{error}</p>
        ) : null}

        <button
          type="button"
          onClick={() => setShowEmailForm(true)}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-accent-500"
        >
          {t('Inloggen met e-mail')}
        </button>

        <div className="mt-6 border-t border-surface-800 pt-5 text-center text-sm text-surface-400">
          {t('Nog geen account?')}{' '}
          <Link to="/registreren" className="font-medium text-accent-400 hover:text-accent-300">
            {t('Maak er een aan')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-md">
      {isRegistrationEnabled ? (
        <button
          type="button"
          onClick={() => setShowEmailForm(false)}
          className="mb-4 flex items-center gap-1.5 text-sm text-surface-500 transition hover:text-surface-300"
        >
          ← {t('Terug')}
        </button>
      ) : null}

      <h2 className="mb-2 text-xl font-semibold text-surface-100">{t('Inloggen met e-mail')}</h2>
      <p className="mb-6 text-sm text-surface-400">{t('Meld je aan met je e-mailadres.')}</p>

      {!isRegistrationEnabled ? (
        <p className="mb-6 rounded-lg bg-surface-800 px-4 py-2.5 text-sm text-surface-400">
          {t('Registratie is tijdelijk niet mogelijk. Heb je al een account? Log dan hieronder in met je e-mailadres en wachtwoord.')}
        </p>
      ) : null}

      <form onSubmit={handleEmailSubmit} noValidate className="flex flex-col gap-4">
        <div>
          <label htmlFor="login-email" className="mb-1.5 block text-sm text-surface-300">
            {t('E-mailadres')}
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={submitting}
            className="w-full rounded-xl border border-surface-700 bg-surface-800 px-4 py-3 text-sm text-surface-100 placeholder-surface-500 transition focus:border-accent-500 focus:outline-none disabled:opacity-50"
              placeholder={t('naam@voorbeeld.nl')}
          />
        </div>

        <div>
          <div className="mb-1.5">
            <label htmlFor="login-password" className="text-sm text-surface-300">
              {t('Wachtwoord')}
            </label>
          </div>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={submitting}
            className="w-full rounded-xl border border-surface-700 bg-surface-800 px-4 py-3 text-sm text-surface-100 placeholder-surface-500 transition focus:border-accent-500 focus:outline-none disabled:opacity-50"
            placeholder="••••••••"
          />
          <Link
            to="/wachtwoord-vergeten"
            className="mt-2 inline-block text-xs text-surface-500 transition hover:text-surface-300"
          >
            {t('Wachtwoord vergeten?')}
          </Link>
        </div>

        {error ? (
          <p className="rounded-lg bg-red-950/60 px-4 py-2.5 text-sm text-red-400">{error}</p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || !email.trim() || !password}
          className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loadingEmail ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-accent-300 border-t-white" />
          ) : null}
          {t('Inloggen')}
        </button>
      </form>

      {isRegistrationEnabled ? (
        <div className="mt-6 border-t border-surface-800 pt-5 text-center text-sm text-surface-400">
          {t('Nog geen account?')}{' '}
          <Link to="/registreren" className="font-medium text-accent-400 hover:text-accent-300">
            {t('Maak er een aan')}
          </Link>
        </div>
      ) : null}
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z"
        fill="#EA4335"
      />
    </svg>
  )
}
